import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { Box, Button, TextField, Typography, MenuItem, Select, FormControl, InputLabel, LinearProgress } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import RecordContentModal from '../components/RecordContentModal';

const EditCoursePage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [newTopic, setNewTopic] = useState({ mainTopic: '', subParts: [] });
  const [newSubPart, setNewSubPart] = useState({ title: '', content: [] });
  const [newContent, setNewContent] = useState({ type: 'video', url: '' });
  const [contentType, setContentType] = useState('video');
  const [uploadProgress, setUploadProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourse = async () => {
      const courseDoc = doc(db, 'courses', courseId);
      const courseSnapshot = await getDoc(courseDoc);
      if (courseSnapshot.exists()) {
        setCourse(courseSnapshot.data());
      }
    };
    fetchCourse();
  }, [courseId]);

  const handleUpdateCourse = async () => {
    if (!course.name || !course.price || !course.author || !course.image) {
      alert("Please fill in all required fields.");
      return;
    }

    if (isNaN(course.price)) {
      alert("Price must be a valid number.");
      return;
    }

    const courseDoc = doc(db, 'courses', courseId);
    await updateDoc(courseDoc, course);
    navigate('/admin');
  };

  const handleAddTopic = () => {
    setCourse({ ...course, curriculum: [...course.curriculum, newTopic] });
    setNewTopic({ mainTopic: '', subParts: [] });
  };

  const handleAddSubPart = () => {
    const updatedCurriculum = course.curriculum.map((topic, index) => {
      if (index === course.curriculum.length - 1) {
        return { ...topic, subParts: [...topic.subParts, newSubPart] };
      }
      return topic;
    });
    setCourse({ ...course, curriculum: updatedCurriculum });
    setNewSubPart({ title: '', content: [] });
  };

  const handleAddContent = async () => {
    if (newContent.type === 'firebaseVideo' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_VIDEO_SIZE) {
        alert("Video file size must be less than 15 MB.");
        return;
      }
      const storageRef = ref(storage, `videos/${course.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else if (newContent.type === 'pdf' && newContent.file) {
      if (newContent.file.size > process.env.REACT_APP_MAX_PDF_SIZE) {
        alert("PDF file size must be less than 5 MB.");
        return;
      }
      const storageRef = ref(storage, `pdfs/${course.name}/${newContent.file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, newContent.file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          console.error('Upload failed', error);
        }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const contentWithoutFile = { ...newContent, url };
          delete contentWithoutFile.file;
          finalizeContentAddition(contentWithoutFile);
        }
      );
    } else {
      finalizeContentAddition(newContent);
    }
  };

  const finalizeContentAddition = (content) => {
    const updatedCurriculum = course.curriculum.map((topic, index) => {
      if (index === course.curriculum.length - 1) {
        const updatedSubParts = topic.subParts.map((subPart, subIndex) => {
          if (subIndex === topic.subParts.length - 1) {
            return { ...subPart, content: [...subPart.content, content] };
          }
          return subPart;
        });
        return { ...topic, subParts: updatedSubParts };
      }
      return topic;
    });
    setCourse({ ...course, curriculum: updatedCurriculum });
    setNewContent({ type: 'video', url: '' });
    setUploadProgress(null);
  };

  const handleRecordedContent = (content) => {
    finalizeContentAddition(content);
    setContentType('video');
  };

  if (!course) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress />
        <Typography variant="h5" sx={{ mt: 3, textAlign: 'center' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" gutterBottom>Edit Course</Typography>
      <TextField label="Name" value={course.name} onChange={(e) => setCourse({ ...course, name: e.target.value })} fullWidth />
      <TextField label="Description" value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} fullWidth />
      <TextField label="Video URL" value={course.videoUrl} onChange={(e) => setCourse({ ...course, videoUrl: e.target.value })} fullWidth />
      <TextField label="Price" value={course.price} onChange={(e) => setCourse({ ...course, price: e.target.value })} fullWidth />
      <TextField label="Author" value={course.author} onChange={(e) => setCourse({ ...course, author: e.target.value })} fullWidth />
      <TextField label="Image URL" value={course.image} onChange={(e) => setCourse({ ...course, image: e.target.value })} fullWidth />
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Curriculum</Typography>
      <TextField label="Topic" value={newTopic.mainTopic} onChange={(e) => setNewTopic({ ...newTopic, mainTopic: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddTopic}>Add Topic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Subtopic</Typography>
      <TextField label="Subtopic" value={newSubPart.title} onChange={(e) => setNewSubPart({ ...newSubPart, title: e.target.value })} fullWidth />
      <Button variant="contained" onClick={handleAddSubPart}>Add Subtopic</Button>
      
      <Typography variant="h6" sx={{ mt: 4 }}>Add Content</Typography>
      <FormControl fullWidth>
        <InputLabel>Content Type</InputLabel>
        <Select
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
        >
          <MenuItem value="video">Video (YouTube)</MenuItem>
          <MenuItem value="pdf">PDF</MenuItem>
          <MenuItem value="firebaseVideo">Video (Firebase Storage)</MenuItem>
          <MenuItem value="record">Record Content (Audio/Video)</MenuItem>
        </Select>
      </FormControl>
      {contentType === 'record' ? (
        <RecordContentModal
          open={true}
          onClose={() => setContentType('video')}
          onContentRecorded={handleRecordedContent}
          courseName={course.name}
        />
      ) : contentType === 'firebaseVideo' || contentType === 'pdf' ? (
        <>
          <TextField
            type="file"
            onChange={(e) => setNewContent({ ...newContent, type: contentType, file: e.target.files[0] })}
            fullWidth
          />
          {uploadProgress !== null && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              {uploadProgress === 100 && <Typography variant="body2" color="success.main">Upload complete!</Typography>}
            </Box>
          )}
        </>
      ) : (
        <TextField
          label="Content URL"
          value={newContent.url}
          onChange={(e) => setNewContent({ ...newContent, type: contentType, url: e.target.value })}
          fullWidth
        />
      )}
      <Button variant="contained" onClick={handleAddContent} sx={{ mb: 8 }}>Add Content</Button>
      
      <Button variant="outlined" onClick={handleUpdateCourse} sx={{ mt: 6, position: 'relative', right: 138 }}>Update Course</Button>
    </Box>
  );
};

export default EditCoursePage;
